import axios from "axios";
import {
  hslToHex,
  ServiceSettings,
  Suit,
  UserBranding
} from "@madhive/mad-sdk";
import { madSDK } from "lib/sdk";
import { isValidNumber } from "./number";

const DEFAULT_FALLBACK_COLOR = "#FFF";

export const getVariable = (variable: any, scope = document.body) =>
  getComputedStyle(scope).getPropertyValue(variable).trim();

export const isValidHexFormat = (c: string) =>
  Boolean(
    typeof c === "string" &&
      c.startsWith("#") &&
      (c.length === 4 || c.length === 7) &&
      !/[^a-fA-F0-9]/.test(c.slice(1))
  );

export const convertRGBColorComponentToHex = (c: string | number) => {
  const hex = Number(c).toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

export const convertRBGStringToRGBComponents = (c: string) => {
  if (typeof c !== "string") {
    return [];
  }

  if (!c.startsWith("rgb(") || !c.endsWith(")")) {
    return [];
  }

  return c
    .trim()
    .replace("rgb(", "")
    .replace(")", "")
    .split(",")
    .map(v => v.trim());
};

export const isValidRGBFormat = (c: string) => {
  if (typeof c !== "string") {
    return false;
  }

  const rgbComponents = convertRBGStringToRGBComponents(c);

  if (rgbComponents.length !== 3) {
    return false;
  }

  return rgbComponents.every(v => {
    const i = parseInt(v, 10);
    return isValidNumber(i) && i >= 0 && i <= 255;
  });
};

export const convertRGBStringToHexString = (c: string) => {
  if (!isValidRGBFormat(c)) {
    return DEFAULT_FALLBACK_COLOR;
  }

  const [r, g, b] = convertRBGStringToRGBComponents(c);

  const hex = `#${convertRGBColorComponentToHex(
    r
  )}${convertRGBColorComponentToHex(g)}${convertRGBColorComponentToHex(b)}`;

  /**
   * If invalid hex format, fallback to default white color
   */
  return isValidHexFormat(hex) ? hex : DEFAULT_FALLBACK_COLOR;
};

/**
 * Picks the suit burns would render as `.default-theme`: the one flagged
 * is_default, falling back to the first.
 */
export const selectDefaultSuit = (
  settings?: ServiceSettings
): Suit | undefined => {
  const suits = settings?.suits;
  if (!Array.isArray(suits) || suits.length === 0) {
    return undefined;
  }
  return suits.find(suit => suit?.is_default) ?? suits[0];
};

/**
 * Mirrors makeCssVariables in services/burns/src/assets/styles/themes.ts:
 * underscores become dashes, and each colour is emitted twice — once as bare
 * HSL components (`--x-hsl`, so callers can compose their own alpha) and once
 * as a ready hsl() value.
 */
export const suitToCustomProperties = (suit: Suit): Array<[string, string]> => {
  const props: Array<[string, string]> = [];

  Object.entries(suit.colors ?? {}).forEach(([name, hsl]) => {
    if (
      typeof hsl?.hue !== "number" ||
      typeof hsl?.saturation !== "number" ||
      typeof hsl?.lightness !== "number"
    ) {
      return;
    }
    const cssName = name.replace(/_/g, "-");
    const components = `${hsl.hue}, ${hsl.saturation}%, ${hsl.lightness}%`;
    props.push([`--${cssName}-hsl`, components]);
    props.push([`--${cssName}`, `hsl(${components})`]);
  });

  Object.entries(suit.styles ?? {}).forEach(([name, value]) => {
    if (typeof value !== "string") {
      return;
    }
    props.push([`--${name.replace(/_/g, "-")}`, value]);
  });

  return props;
};

/**
 * Brand colours derived from an org's legacy HSL theme, as burns generated
 * them. hslToHex mutates its argument, so each call gets its own object.
 */
export const legacyThemeToCustomProperties = (
  theme: ServiceSettings["theme"]
): Array<[string, string]> => {
  if (!theme) {
    return [];
  }
  const { hue, saturation, lightness } = theme;
  const full =
    hue !== undefined && saturation !== undefined && lightness !== undefined;
  const from = (h: number, s: number, l: number) =>
    hslToHex({ hue: h, saturation: s, lightness: l });
  const at = (satDelta: number) =>
    full ? from(hue, saturation - satDelta, lightness) : hslToHex(undefined);

  return [
    ["--primary-color", at(0)],
    ["--primary-hover", at(10)],
    ["--primary-color-light", from(hue ?? 0, 95, 100)],
    ["--primary-focus", at(20)],
    ["--ghost-hover", from(hue ?? 0, 96, 100)],
    ["--ghost-focus", from(hue ?? 0, 88, 100)]
  ];
};

/**
 * Splits the `legacy-overrides` setting, raw declarations burns pasted into a
 * rule, into [property, value] pairs. A `;` inside quotes or parentheses
 * (data URLs) does not end a declaration.
 */
export const parseLegacyOverrides = (raw: unknown): Array<[string, string]> => {
  if (typeof raw !== "string") {
    return [];
  }
  const text = raw.replace(/\\n/g, "\n");
  const declarations: string[] = [];
  let depth = 0;
  let quote = "";
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "\\") {
      i += 1; // an escaped character never opens or closes anything
    } else if (quote) {
      if (c === quote) quote = "";
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (c === "(") {
      depth += 1;
    } else if (c === ")") {
      depth = Math.max(0, depth - 1);
    } else if (c === ";" && depth === 0) {
      declarations.push(text.slice(start, i));
      start = i + 1;
    }
  }
  declarations.push(text.slice(start));

  return declarations.flatMap(d => {
    const at = d.indexOf(":");
    const name = d.slice(0, at).trim();
    const value = d.slice(at + 1).trim();
    return at > 0 && name && value ? [[name, value] as [string, string]] : [];
  });
};

/**
 * Applies the organization's default suit and/or JWT branding claims as
 * inline custom properties on document.body.
 */
export const applyOrgBranding = (
  settings?: ServiceSettings,
  branding?: UserBranding
) => {
  // Logout navigates rather than reloading, so the next account re-runs this
  // against the same body. Anything the outgoing org set and the incoming one
  // does not would otherwise persist.
  document.body.removeAttribute("style");

  const suit = selectDefaultSuit(settings);
  if (suit) {
    suitToCustomProperties(suit).forEach(([name, value]) => {
      document.body.style.setProperty(name, value);
    });
  }
  // Legacy HSL theme, then the org's own overrides, over the suit as burns'
  // theme.css did; the claim below still wins for the accent colours.
  legacyThemeToCustomProperties(settings?.theme).forEach(([name, value]) => {
    document.body.style.setProperty(name, value);
  });
  parseLegacyOverrides(settings?.values?.["legacy-overrides"]).forEach(
    ([name, value]) => {
      document.body.style.setProperty(name, value);
    }
  );
  // `--primary-color` is the brand accent; `--color-primary` is body text
  // (burns legacy.css). Identity's own GenerateThemeCSS writes the claim to
  // the former, so the two stay in agreement.
  if (branding?.primaryColor && isValidHexFormat(branding.primaryColor)) {
    document.body.style.setProperty("--primary-color", branding.primaryColor);
  }
  if (branding?.secondaryColor && isValidHexFormat(branding.secondaryColor)) {
    document.body.style.setProperty(
      "--secondary-color",
      branding.secondaryColor
    );
  }
};

/**
 * Before sign-in there is no account to take branding from, so ask identity
 * for the colour of the org that owns this domain. Best effort: an
 * unreachable or older identity just leaves the default theme.
 */
const fetchPublicBranding = async (): Promise<UserBranding | undefined> => {
  try {
    const { data } = await axios.get<{ primaryColor?: string }>(
      `${madSDK.urls.identityBaseUrl}/public/branding`,
      { params: { domain: window.location.hostname }, timeout: 2000 }
    );
    return data?.primaryColor
      ? ({ primaryColor: data.primaryColor } as UserBranding)
      : undefined;
  } catch {
    return undefined;
  }
};

/** Pre-login styling, from the domain alone. */
export const initializePublicStyles = async () => {
  applyOrgBranding(undefined, await fetchPublicBranding());
};

export const initializeStyles = async (
  settings?: ServiceSettings,
  branding?: UserBranding
) => {
  applyOrgBranding(settings, branding);
};

// Replaces Material UI breakpoint values
// https://v4.mui.com/customization/breakpoints/#default-breakpoints
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920
};

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | number;

const getBreakpointValue = (breakpoint: Breakpoint): number =>
  isValidNumber(breakpoint) ? breakpoint : BREAKPOINTS[breakpoint];

export const buildMediaQuery = {
  min: (min: Breakpoint) => `@media (min-width: ${getBreakpointValue(min)}px)`,
  max: (max: Breakpoint) =>
    `@media (max-width: ${getBreakpointValue(max) - 0.5}px)`,
  between: (min: Breakpoint, max: Breakpoint) =>
    `@media (min-width: ${getBreakpointValue(min)}px) and (max-width: ${
      getBreakpointValue(max) - 0.5
    }px)`
};
